<template>
  <div id="contract-details-summary">
    <div class="m-b-20">
      <div class="bgm-lightgray p-15">
        <WdsText large>Engagement</WdsText>
      </div>
      <hr class="m-t-0" />
      <div class="p-l-15 p-r-15">
        <div class="row">
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn
                :field-label="$t(fieldTitles.job_name)"
                :prevent-edits="!editable || !isViewingAsCompany"
                @toggle-edit="focus('job_name')"
              >
                <template v-if="canShowUpdatedLabelByField('job_name')" #content-extra-for-label>
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('job_name')"
                    :text="getFieldByKey('job_name')?.initial"
                    strike-through
                  />
                  {{ contract.job_name }}
                </template>
                <template #content-for-edit-mode>
                  <input
                    ref="job_name"
                    v-model="contract.job_name"
                    :placeholder="$t('Enter title')"
                    class="form-control f-14"
                    name="job_name"
                    required="required"
                    type="text"
                  />
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('job_name')"
                    :initial="getFieldByKey('job_name')?.initial"
                    field="job_name"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn
                :default-to-edit-mode="
                  (requireDateStart && !contract.date_start) ||
                  (requireDateEnd && !contract.date_end)
                "
                :field-label="$t('Timeframe')"
                :prevent-edits="!editable || !isViewingAsCompany"
              >
                <template v-if="canShowTimeframeUpdatedLabel()" #content-extra-for-label>
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isTimeframeChanged()"
                    :text="getTimeframeDisplayText()"
                    strike-through
                  />
                  {{ timeFrameDisplayText }}
                </template>
                <template #content-for-edit-mode>
                  <div class="row">
                    <div class="col-xs-6">
                      <PickerDatetime
                        v-model="contract.date_start"
                        :disabled="dateStartDisabled"
                        :label="$t('From') + ':'"
                        :placeholder="$t('Select here')"
                        :required="requireDateStart"
                        as-input
                        class="f-14"
                        float-label
                        format="YYYY-MM-DD"
                        name="date_start"
                        no-icon
                      />
                    </div>
                    <div class="col-xs-6">
                      <PickerDatetime
                        v-model="contract.date_end"
                        :label="$t('To') + ':'"
                        :min-date="contract.date_start"
                        :placeholder="$t('Select here')"
                        :required="requireDateEnd"
                        as-input
                        class="f-14"
                        float-label
                        format="YYYY-MM-DD"
                        name="date_end"
                        no-icon
                      />
                    </div>
                  </div>
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn
                :default-to-edit-mode="isViewingAsCompany && requirePoNumber && !contract.po_number"
                :field-label="$t('PO number')"
                :prevent-edits="!editable || !isViewingAsCompany"
                @toggle-edit="focus('input-po-number')"
              >
                <template v-if="canShowUpdatedLabelByField('po_number')" #content-extra-for-label>
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('po_number')"
                    :text="getFieldByKey('po_number')?.initial"
                    strike-through
                  />
                  {{ contract.po_number }}
                </template>
                <template #content-for-edit-mode>
                  <small v-if="!requirePoNumber" class="c-gray">
                    {{ $t('If applicable') }}
                  </small>
                  <small v-else class="c-gray">
                    {{ $t('Required on all hires') }}
                  </small>
                  <br />
                  <input
                    id="input-po-number"
                    ref="input-po-number"
                    v-model="contract.po_number"
                    :placeholder="$t('Enter PO number')"
                    :required="requirePoNumber"
                    :title="
                      $t(
                        perspective === 'company'
                          ? 'Your PO number for the contract'
                          : 'PO number for the contract',
                      )
                    "
                    class="form-control"
                    data-placement="top"
                    data-toggle="tooltip"
                    name="po_number"
                    maxlength="255"
                    type="text"
                  />
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('po_number')"
                    :initial="getFieldByKey('po_number')?.initial"
                    field="po_number"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
          <div class="col-sm-6 existing-job-rate-container">
            <p>
              <BaseFieldWithEditBtn
                :field-label="$t('Rate')"
                :prevent-edits="!canEditRate || hasMilestones"
              >
                <template v-if="canShowUpdatedLabelByField('rate')" #content-extra-for-label>
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template v-if="contract.ratetype" #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('rate')"
                    :text="`${contract.currency} ${getFieldByKey('rate')?.initial}`"
                    strike-through
                  />
                  <CurrencyDisplay
                    :converted-currency-code="account.currency"
                    disable-convert-currency-modal
                    :currency-code="contract.currency"
                    :range-from="null"
                    :range-to="null"
                    :rate="Number(contract.rate)"
                    :show-converted-rates-by-default="false"
                    :show-currency-code="true"
                  />
                  ({{ hasMilestones ? $t('Milestones') : $t(contract.ratetype) }})
                </template>
                <template #content-for-edit-mode>
                  <div class="tw-mb-2">
                    <ContractFormRateTypeRadio v-model="contract.ratetype" />
                  </div>
                  <ContractFormRateCurrency
                    :enable-multiple-currencies="
                      enableMultiCurrencyContracts ||
                      (!enableMultiCurrencyContracts && preferWorkerCurrency)
                    "
                    :initial-rate="rate"
                    :initial-rate-exchanged="rateExchanged"
                    :initial-prefer-worker-currency="preferWorkerCurrency"
                    :rate-type="contract.ratetype"
                    :company-currency="companyCurrencyConfig.currencySymbol"
                    :worker-currency="freelancerCurrencyConfig.currencySymbol"
                    :minimum-rate="1"
                    @toggle:prefer-currency="onTogglePreferCurrency"
                    @update:rate="onRateUpdated"
                    @update:rate-exchanged="onRateExchangedUpdated"
                  />
                  <input :value="contract.rate" name="rate" type="hidden" />
                  <input
                    :value="
                      preferWorkerCurrency
                        ? freelancerCurrencyConfig.currencySymbol
                        : companyCurrencyConfig.currencySymbol
                    "
                    name="currency"
                    type="hidden"
                  />
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('rate')"
                    :fields="['ratetype']"
                    :initial="`${contract.currency} ${getFieldByKey('rate')?.initial}`"
                    field="rate"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn
                :field-label="$t(fieldTitles.job_location_preference)"
                :prevent-edits="!editable || !isViewingAsCompany"
              >
                <template
                  v-if="canShowUpdatedLabelByField('job_location_preference')"
                  #content-extra-for-label
                >
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('job_location_preference')"
                    :text="
                      locationPreferenceLabel(getFieldByKey('job_location_preference')?.initial)
                    "
                    strike-through
                  />
                  {{ locationPreferenceLabel(contract.job_location_preference) }}
                </template>
                <template #content-for-edit-mode>
                  <div
                    id="input-job_location_preference"
                    ref="input-job_location_preference"
                    class="m-t-5"
                  >
                    <div
                      v-for="option in ['onsite-only', 'remote-only']"
                      :key="option"
                      class="radio input-btn m-t-0"
                    >
                      <label class="f-13 f-500 btn-block">
                        <input
                          v-model="contract.job_location_preference"
                          :value="option"
                          name="job_location_preference"
                          type="radio"
                          :data-test="'contract-location-preference-' + option"
                        />
                        <i class="input-helper m-r-5"></i>
                        {{ locationPreferenceLabel(option) }}
                      </label>
                    </div>
                  </div>
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('job_location_preference')"
                    :initial="
                      locationPreferenceLabel(getFieldByKey('job_location_preference')?.initial)
                    "
                    field="job_location_preference"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn
                :field-label="$t('Work address')"
                :prevent-edits="!editable || !isViewingAsCompany"
                @toggle-edit="wantsToFillAddress = true"
              >
                <template v-if="canShowAddressUpdatedLabel()" #content-extra-for-label>
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText v-if="isAddressChanged()" :text="initialFullAddress" strike-through />

                  <div class="tw-flex tw-flex-col tw-gap-2">
                    <div>{{ fullAddress }}</div>

                    <WdsAlert v-if="isViewingAsCompany && !isAccepted && !isAddressFilled">
                      {{
                        $t(
                          "We've introduced structured address fields. Please update the address for accurate work location details.",
                        )
                      }}
                    </WdsAlert>
                  </div>
                </template>
                <template #content-for-edit-mode>
                  <input type="hidden" name="wants_to_fill_address" :value="wantsToFillAddress" />
                  <ContractFormAddressInput
                    ref="address"
                    v-model:address="contract.address"
                    v-model:city="contract.city"
                    v-model:postcode="contract.zipcode"
                    v-model:country="contract.country"
                    v-model:state="contract.state"
                    address-input-name="meta[address]"
                    city-input-name="meta[city]"
                    zipcode-input-name="meta[zipcode]"
                    country-input-name="meta[country]"
                    state-input-name="meta[state]"
                  />
                  <ContractFormFieldsUndo
                    v-if="isAddressChanged()"
                    :fields="['address', 'city', 'zipcode', 'country', 'state']"
                    :initial="initialFullAddress"
                    field="address"
                    @undo="resetToInitialField"
                  />
                  <WdsAlert v-if="isViewingAsCompany && !isAccepted && !isAddressFilled">
                    {{
                      $t(
                        "We've introduced structured address fields. Please update the address for accurate work location details.",
                      )
                    }}
                  </WdsAlert>
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-12">
            <p>
              <BaseFieldWithEditBtn
                :field-label="$t(fieldTitles.job_description)"
                :prevent-edits="!editable || !isViewingAsCompany"
                @toggle-edit="focus('job_description')"
              >
                <template
                  v-if="canShowUpdatedLabelByField('job_description')"
                  #content-extra-for-label
                >
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('job_description')"
                    :text="truncateText(getFieldByKey('job_description')?.initial, 100)"
                    strike-through
                  />
                  <BaseShowMore
                    :collapsable="true"
                    :collapse-text="$t('Show less')"
                    :collapsed-height="65"
                    :delay="500"
                    :expand-text="$t('Show more')"
                    align-with-text
                  >
                    <span style="white-space: pre-line; overflow: auto">
                      {{ contract.job_description }}
                    </span>
                  </BaseShowMore>
                </template>
                <template #content-for-edit-mode>
                  <textarea
                    ref="job_description"
                    v-model="contract.job_description"
                    :placeholder="$t('Enter description')"
                    class="form-control f-14"
                    name="job_description"
                    rows="4"
                  />
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('job_description')"
                    :hide-initial="true"
                    field="job_description"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-12">
            <p class="tw-mb-0">
              <span v-if="contract.factoring_days != null">
                <WdsAlert
                  v-if="!isViewingAsCompany"
                  secondary
                  icon
                  :title="$t('Payment terms')"
                  bordered
                >
                  <template #description>
                    {{
                      $t(
                        'Your bill will be paid :days days after it is created provided it has been approved by the client.',
                        {
                          days: contract.factoring_days,
                        },
                      )
                    }}
                  </template>
                </WdsAlert>
                <template v-if="isViewingAsCompany">
                  <WdsAlert secondary icon :title="$t('Payment terms')" bordered>
                    <template #description>
                      {{
                        $t('Worksome will pay the freelancer’s approved bills within :days days.', {
                          days: contract.factoring_days,
                        })
                      }}
                    </template>
                  </WdsAlert>
                  <div class="row">
                    <div class="col-6">
                      <input v-model="contract.payment_term" name="payment_term" type="hidden" />
                      <input
                        v-model="contract.payment_term_method"
                        name="payment_term_method"
                        type="hidden"
                      />
                    </div>
                  </div>
                </template>
              </span>
              <span v-else>
                <BaseFieldWithEditBtn :field-label="$t('Payment terms')" :prevent-edits="true">
                  <template
                    v-if="canShowUpdatedLabelByField('payment_term')"
                    #content-extra-for-label
                  >
                    <WdsLabel subtle success>Updated</WdsLabel>
                  </template>
                  <template #content-for-display-mode>
                    <input v-model="contract.payment_term" name="payment_term" type="hidden" />
                    <input
                      v-model="contract.payment_term_method"
                      name="payment_term_method"
                      type="hidden"
                    />
                    <WdsText
                      v-if="isFieldByKeyUpdated('payment_term')"
                      :text="
                        getChangedPaymentTermDisplayText(
                          getFieldByKey('payment_term')?.initial,
                          getFieldByKey('payment_term_method')?.initial,
                        )
                      "
                      strike-through
                    />
                    {{ paymentTermDisplayText }}
                  </template>
                </BaseFieldWithEditBtn>
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="m-b-20">
      <div class="bgm-lightgray p-15">
        <WdsText large>{{ $t('Worker') }}</WdsText>
      </div>
      <hr class="m-t-0" />
      <div class="p-l-15 p-r-15">
        <div class="row">
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn :field-label="$t('Worker’s name')" :prevent-edits="true">
                <template #content-for-display-mode>
                  <div>{{ contract.freelancer_name }}</div>
                  <WdsLabel v-if="contract.recruiter_manages_workers" sm subtle gray>
                    <div class="tw-flex tw-items-center tw-justify-center">
                      <WdsIcon color="#0E0D12" name="group" weight="thick" :size="12" />
                      <span class="tw-ml-2">{{ $t('Managed worker') }}</span>
                    </div>
                  </WdsLabel>
                </template>
              </BaseFieldWithEditBtn>
            </p>
            <p
              v-if="contract.recruiter_manages_workers && contract.recruiter_contact"
              class="tw-mb-0"
            >
              <BaseFieldWithEditBtn
                :field-label="$t('Staffing agency contact person')"
                :prevent-edits="true"
              >
                <template #content-for-display-mode>
                  {{ contract.recruiter_contact }}
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
          <div v-if="contract.recruiter_company" class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn :field-label="$t('Staffing agency name')" :prevent-edits="true">
                <template #content-for-display-mode>
                  {{ contract.recruiter_company }}
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="m-b-20">
      <div class="bgm-lightgray p-15">
        <WdsText large>{{ $t('Company details') }}</WdsText>
      </div>
      <hr class="m-t-0" />
      <div class="p-l-15 p-r-15">
        <div class="row">
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn
                :default-to-edit-mode="!contract.company_name && isViewingAsCompany && editable"
                :field-label="companyNameLabelText"
                :prevent-edits="!editable || !isViewingAsCompany || hasMultipleBusinessEntities"
                @toggle-edit="handleToggleEdit('company_name')"
              >
                <template
                  v-if="canShowUpdatedLabelByField('company_name')"
                  #content-extra-for-label
                >
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('company_name')"
                    :text="getFieldByKey('company_name')?.initial"
                    strike-through
                  />
                  {{ contract.company_name }}
                </template>
                <template #content-for-edit-mode>
                  <input
                    ref="company_name"
                    v-model="contract.company_name"
                    :placeholder="$t('Enter company name')"
                    class="form-control f-14"
                    name="meta[company]"
                    required="required"
                    type="text"
                  />
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('company_name')"
                    :initial="getFieldByKey('company_name')?.initial"
                    field="company_name"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
            <p>
              <BaseFieldWithEditBtn
                :default-to-edit-mode="!contract.company_cvr && isViewingAsCompany && editable"
                :field-label="$t(cvrTerminology)"
                :prevent-edits="!editable || !isViewingAsCompany"
                @toggle-edit="handleToggleEdit('company_cvr')"
              >
                <template v-if="canShowUpdatedLabelByField('company_cvr')" #content-extra-for-label>
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('company_cvr')"
                    :text="getFieldByKey('company_cvr')?.initial"
                    strike-through
                  />
                  {{ contract.company_cvr }}
                </template>
                <template #content-for-edit-mode>
                  <input
                    ref="company_cvr"
                    v-model="contract.company_cvr"
                    :placeholder="$t('Enter ' + cvrTerminology)"
                    class="form-control f-14"
                    name="meta[cvr]"
                    required="required"
                    type="text"
                  />
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('company_cvr')"
                    :initial="getFieldByKey('company_cvr')?.initial"
                    field="company_cvr"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
            <p>
              <BaseFieldWithEditBtn
                :field-label="$t('Contact person')"
                :prevent-edits="!editable || companyContactsCount < 2 || !isViewingAsCompany"
                @toggle-edit="focus('company_contact_user_id')"
              >
                <template
                  v-if="canShowUpdatedLabelByField('company_contact_user_id')"
                  #content-extra-for-label
                >
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isFieldByKeyUpdated('company_contact_user_id')"
                    :text="companyContacts[getFieldByKey('company_contact_user_id')?.initial]"
                    strike-through
                  />
                  {{ companyContactName }}
                  <span
                    v-if="perspective === 'company' && companyContactsCount < 2"
                    class="small c-gray"
                  >
                    {{ $t('With our Teams feature, you can have additional contacts') }}
                  </span>
                </template>
                <template #content-for-edit-mode>
                  <select
                    id="select_contact_user_id"
                    ref="company_contact_user_id"
                    v-model="contract.company_contact_user_id"
                    class="form-control f-14"
                    name="company_contact_user_id"
                  >
                    <option v-for="(name, i) in companyContacts" :key="i" :value="i">
                      {{ name }}
                    </option>
                  </select>
                  <ContractFormFieldsUndo
                    v-if="isFieldByKeyUpdated('company_contact_user_id')"
                    :initial="companyContacts[getFieldByKey('company_contact_user_id')?.initial]"
                    field="company_contact_user_id"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
          <div class="col-sm-6">
            <p>
              <BaseFieldWithEditBtn
                :default-to-edit-mode="
                  (!contract.company_address ||
                    !contract.company_zipcode ||
                    !contract.company_city) &&
                  isViewingAsCompany &&
                  editable
                "
                :field-label="companyAddressLabelText"
                :prevent-edits="!editable || !isViewingAsCompany || hasMultipleBusinessEntities"
                @toggle-edit="toggleEditCompanyAddress"
              >
                <template v-if="canShowCompanyAddressUpdatedLabel()" #content-extra-for-label>
                  <WdsLabel subtle success>Updated</WdsLabel>
                </template>
                <template #content-for-display-mode>
                  <WdsText
                    v-if="isCompanyAddressChanged() && !isViewingAsCompany"
                    :text="initialFullCompanyAddress"
                    strike-through
                  />
                  {{ fullCompanyAddress }}
                </template>
                <template #content-for-edit-mode>
                  <input
                    type="hidden"
                    name="wants_to_fill_company_address"
                    :value="wantsToFillCompanyAddress"
                  />
                  <ContractFormAddressInput
                    ref="address"
                    v-model:address="contract.company_address"
                    v-model:city="contract.company_city"
                    v-model:postcode="contract.company_zipcode"
                    v-model:country="contract.company_country"
                    v-model:state="contract.company_state"
                    address-input-name="meta[company_address]"
                    city-input-name="meta[company_city]"
                    zipcode-input-name="meta[company_zipcode]"
                    country-input-name="meta[company_country]"
                    state-input-name="meta[company_state]"
                  />
                  <ContractFormFieldsUndo
                    v-if="isCompanyAddressChanged()"
                    :fields="[
                      'company_address',
                      'company_city',
                      'company_zipcode',
                      'company_country',
                      'company_state',
                    ]"
                    :initial="initialFullCompanyAddress"
                    field="company_address"
                    @undo="resetToInitialField"
                  />
                </template>
              </BaseFieldWithEditBtn>
            </p>
          </div>
        </div>

        <transition name="fade">
          <div
            v-if="
              isViewingAsCompany &&
              (hasBillContactUserId || advancedFields.includes('bill_contact_user_id'))
            "
          >
            <hr class="m-t-10 m-b-15" />
            <div class="row">
              <div class="col-sm-12">
                <BaseFieldWithEditBtn
                  :default-to-edit-mode="isViewingAsCompany && !contract.bill_contact_user_id"
                  :field-label="$t('Billing contact person')"
                  :prevent-edits="!editable || companyContactsCount < 2 || !isViewingAsCompany"
                  @toggle-edit="focus('bill_contact_user_id')"
                >
                  <template
                    v-if="canShowUpdatedLabelByField('bill_contact_user_id')"
                    #content-extra-for-label
                  >
                    <WdsLabel subtle success>Updated</WdsLabel>
                  </template>
                  <template #content-for-display-mode>
                    <WdsText
                      v-if="isFieldByKeyUpdated('bill_contact_user_id')"
                      :text="companyContacts[getFieldByKey('bill_contact_user_id')?.initial]"
                      strike-through
                    />
                    {{ billContactName }}
                    <span
                      v-if="perspective === 'company' && companyContactsCount < 2"
                      class="small c-gray"
                    >
                      {{ $t('With our Teams feature, you can set a billing contact person') }}
                    </span>
                  </template>
                  <template #content-for-edit-mode>
                    <select
                      id="select_bill_contact_user_id"
                      ref="bill_contact_user_id"
                      v-model="contract.bill_contact_user_id"
                      :title="$t('Billing contact person')"
                      class="form-control f-14"
                      name="bill_contact_user_id"
                    >
                      <option v-for="(name, i) in companyContacts" :key="i" :value="i">
                        {{ name }}
                      </option>
                    </select>
                    <ContractFormFieldsUndo
                      v-if="isFieldByKeyUpdated('bill_contact_user_id')"
                      :initial="companyContacts[getFieldByKey('bill_contact_user_id')?.initial]"
                      field="bill_contact_user_id"
                      @undo="resetToInitialField"
                    />
                  </template>
                </BaseFieldWithEditBtn>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <transition name="fade">
      <div v-if="showAdditionalInformation" class="m-b-20">
        <div class="bgm-lightgray p-15">
          <WdsText large>{{ $t('Additional Information') }}</WdsText>
        </div>
        <hr class="m-t-0" />
        <div class="p-l-15 p-r-15">
          <div class="row">
            <transition name="fade">
              <div
                v-if="
                  isViewingAsCompany &&
                  (hasCustomInvoiceText || advancedFields.includes('custom_invoice_text'))
                "
                class="col-sm-12 m-b-15"
              >
                <BaseFieldWithEditBtn
                  :default-to-edit-mode="
                    isViewingAsCompany &&
                    (advancedFields.includes('custom_invoice_text') ||
                      !contract.custom_invoice_text)
                  "
                  :field-label="$t('Additional invoice text')"
                  :field-help-text="
                    $t(`This will be added to all invoices associated with this hire.`)
                  "
                  :prevent-edits="!editable || !isViewingAsCompany"
                  @toggle-edit="focus('custom_invoice_text')"
                >
                  <template
                    v-if="canShowUpdatedLabelByField('custom_invoice_text')"
                    #content-extra-for-label
                  >
                    <WdsLabel subtle success>Updated</WdsLabel>
                  </template>
                  <template #content-for-display-mode>
                    <WdsText
                      v-if="isFieldByKeyUpdated('custom_invoice_text')"
                      :text="getFieldByKey('custom_invoice_text')?.initial"
                      strike-through
                    />
                    {{ contract.custom_invoice_text }}
                  </template>
                  <template #content-for-edit-mode>
                    <small class="c-gray m-b-10">
                      {{
                        $t(
                          'Add any specific text you want to be included on the invoices we send you for this contract',
                        )
                      }}
                    </small>
                    <br />
                    <BaseTextareaAutoresize
                      ref="custom_invoice_text"
                      v-model="contract.custom_invoice_text"
                      :focus-on-load="!hasCustomInvoiceText"
                      :placeholder="$t('Enter a project number, sales order, or another reference')"
                      :title="$t('Custom invoice text')"
                      name="custom_invoice_text"
                    />
                    <ContractFormFieldsUndo
                      v-if="isFieldByKeyUpdated('custom_invoice_text')"
                      :initial="getFieldByKey('custom_invoice_text')?.initial"
                      field="custom_invoice_text"
                      @undo="resetToInitialField"
                    />
                  </template>
                </BaseFieldWithEditBtn>
              </div>
            </transition>

            <transition name="fade">
              <div
                v-if="hasCustomConditions || advancedFields.includes('conditions')"
                class="col-sm-12"
              >
                <BaseFieldWithEditBtn
                  :default-to-edit-mode="
                    isViewingAsCompany &&
                    (advancedFields.includes('conditions') || !contract.conditions)
                  "
                  :field-label="$t('Additional terms')"
                  :field-help-text="
                    $t(
                      `Please be aware that the terms and conditions of the Worksome platform take precedence over any contractual terms`,
                    )
                  "
                  :prevent-edits="!editable || !isViewingAsCompany"
                  @toggle-edit="focus('conditions')"
                >
                  <template
                    v-if="canShowUpdatedLabelByField('conditions')"
                    #content-extra-for-label
                  >
                    <WdsLabel subtle success>Updated</WdsLabel>
                  </template>
                  <template #content-for-display-mode>
                    <WdsText
                      v-if="isFieldByKeyUpdated('conditions')"
                      :text="getFieldByKey('conditions')?.initial"
                      strike-through
                    />
                    {{ contract.conditions }}
                  </template>
                  <template #content-for-edit-mode>
                    <BaseTextareaAutoresize
                      ref="conditions"
                      v-model="contract.conditions"
                      :focus-on-load="!hasCustomConditions"
                      :placeholder="$t('Enter any additional terms & conditions to the contract')"
                      :title="$t('Additional terms')"
                      name="conditions"
                    />
                    <ContractFormFieldsUndo
                      v-if="isFieldByKeyUpdated('conditions')"
                      :initial="getFieldByKey('conditions')?.initial"
                      field="conditions"
                      @undo="resetToInitialField"
                    />
                  </template>
                </BaseFieldWithEditBtn>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="hasAttachedFiles || advancedFields.includes('attachments') || changedFiles.length"
        class="m-b-20"
      >
        <div class="bgm-lightgray p-15">
          <WdsText large>{{ $t('Documents') }}</WdsText>
        </div>
        <hr class="m-t-0" />
        <div class="p-l-15 p-r-15">
          <div class="row">
            <div class="col-sm-12">
              <ContractFormFiles
                :can-edit="editable"
                :contains-unreviewed-files="hasFileChanges || allFilesHaveBeenRemoved"
                :contract-id="contract.id"
                :files="contract.files.length ? contract.files : changedFiles"
                :files-changed="changedFiles"
                :perspective="perspective"
                :show-changes="hasActiveContract"
                @all-files-reviewed="hasUnreviewedFiles = false"
                @not-all-files-reviewed="hasUnreviewedFiles = true"
                @toggle-has-file-changes="toggleHasLiveFileChanges"
              />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import moment from 'moment'
import { nextTick } from 'vue'
import { WdsAlert, WdsIcon, WdsLabel, WdsText } from '@worksome/wds-components'
import ContractFormFiles from '@/components/ContractFormFiles.vue'
import ContractFormAddressInput from '@/components/ContractFormAddressInput.vue'
import ContractFormRateCurrency from '@/components/ContractFormRateCurrency.vue'
import CurrencyDisplay from '@/components/CurrencyDisplay.vue'
import ContractFormRateTypeRadio from '@/components/ContractFormRateTypeRadio.vue'
import PickerDatetime from '@/components/PickerDatetime.vue'
import { textHelpers } from '@/utils/tools'
import ContractFormFieldsUndo from '@/components/ContractFormFieldsUndo.vue'
import BaseFieldWithEditBtn from '@/components/BaseFieldWithEditBtn.vue'
import { $t } from '@/composables/translation'
import BaseShowMore from '@/components/BaseShowMore.vue'
import BaseTextareaAutoresize from '@/components/BaseTextareaAutoresize.vue'

export default {
  components: {
    BaseTextareaAutoresize,
    BaseShowMore,
    BaseFieldWithEditBtn,
    ContractFormFiles,
    ContractFormAddressInput,
    ContractFormRateTypeRadio,
    PickerDatetime,
    CurrencyDisplay,
    ContractFormFieldsUndo,
    ContractFormRateCurrency,
    WdsAlert,
    WdsIcon,
    WdsLabel,
    WdsText,
  },

  props: {
    purpose: { type: String, default: '' },
    contractBuiltFromOtherModels: {
      type: Object,
      default: () => {},
    },
    immediatelySavableFields: { type: Array, default: () => [] },
    activeContract: {
      type: Object,
      default: () => {},
    },
    draftContract: {
      type: Object,
      default: () => {},
    },
    company: {
      type: Object,
      default: () => {},
    },
    advancedFields: { type: Array, default: () => [] },
    perspective: { type: String, default: 'company' },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    editable: { type: Boolean, default: true },
    companyContacts: { type: Object, required: true },
    isAccepted: { type: Boolean, default: false },
    minimumHourlyRateCompany: { type: Number, required: true },
    minimumFixedRateCompany: { type: Number, required: true },
    minimumHourlyRateFreelancer: { type: Number, required: true },
    minimumFixedRateFreelancer: { type: Number, required: true },
    canEditRate: { type: Boolean, default: false },
    job: {
      type: Object,
      default: () => {},
    },
    hasBills: { type: Boolean, default: false },
    requireDateStart: { type: Boolean, default: false },
    requireDateEnd: { type: Boolean, default: false },
    requirePoNumber: { type: Boolean, default: false },
    canViewContract: { type: Boolean, default: false },
    hireId: { type: Number, default: 0 },
    termsAccepted: { type: Boolean, default: false },
    hasMultipleBusinessEntities: { type: Boolean, default: false },
    hasMilestones: { type: Boolean, default: false },
    companyCurrencyConfig: {
      type: Object,
      default: () => {},
    },
    freelancerCurrencyConfig: {
      type: Object,
      default: () => {},
    },
  },

  emits: [
    'currency-change',
    'changes-added',
    'changes-counted',
    'changes-cleared',
    'changes-needing-approval-added',
    'changes-needing-approval-cleared',
    'toggled-warnings',
    'update:date-end',
  ],

  data() {
    return {
      wantsToFillAddress: false,
      wantsToFillCompanyAddress: false,
      contract: this.initialStateOfContract(),
      initialChangesSnapshot: [],
      customConditionsSelected: false,
      customInvoiceTextSelected: false,
      hasLiveFileChanges: false,
      hasUnreviewedFiles: false,
      fieldTitles: {
        files: 'Files',
        address: 'Work place',
        zipcode: 'Zip code',
        city: 'City',
        country: 'Country',
        state: 'State',
        date_start: 'Start date',
        date_end: 'End date',
        company_contact_user_id: 'Company contact',
        bill_contact_user_id: 'Billing contact person',
        po_number: 'PO number',
        custom_invoice_text: 'Additional invoice text',
        conditions: 'Additional terms and conditions',
        payment_term: 'Payment terms',
        payment_term_method: 'Payment term method',
        company_name: 'Company name',
        company_address: 'Company address',
        company_zipcode: 'Zip code',
        company_city: 'City',
        company_country: 'Country',
        company_state: 'State',
        company_cvr: `Company ${cvrTerminology}`,
        rate: 'Rate',
        ratetype: '',
        job_name: 'Title',
        job_description: 'Description',
        job_location_preference: 'Primary work location',
      },
      hasAttemptedToEditCompanyFields: false,
      rate: '',
      rateExchanged: '',
      preferWorkerCurrency: false,
    }
  },

  computed: {
    countries() {
      return countriesGlobalVar
    },
    cvrTerminology() {
      // globally defined in master.blade
      return cvrTerminology
    },
    hasActiveContract() {
      return Object.keys(this.activeContract).length > 0
    },
    hasDraftContract() {
      return Object.keys(this.draftContract).length > 0
    },
    containsSavableUserChanges() {
      return (
        this.changes.filter(
          (change) =>
            // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
            this.initialStateOfContract()[change.field] != this.contract[change.field] &&
            this.immediatelySavableFields.includes(change.field),
        ).length > 0
      )
    },
    baselineContract() {
      if (this.hasDraftContract) {
        return this.hasActiveContract ? this.activeContract : this.contractBuiltFromOtherModels
      }
      return this.initialStateOfContract()
    },
    comparisonContract() {
      return this.contract
    },
    hasEditsToCompanyFields() {
      const companyFields = [
        'company_name',
        'company_address',
        'company_zipcode',
        'company_city',
        'company_country',
        'company_state',
        'company_cvr',
      ]
      return companyFields.filter((x) => this.changes.map((c) => c.field).includes(x)).length > 0
    },
    hasEditsToImmediatelySaveableFields() {
      return (
        this.immediatelySavableFields.filter((x) => this.changes.map((c) => c.field).includes(x))
          .length > 0
      )
    },
    hasChanges() {
      return this.changes.length > 0
    },
    showChanges() {
      return (
        this.purpose !== 'create' &&
        this.isAccepted &&
        this.hasChanges &&
        // contains changes other than files
        // or all files have been removed from contract
        (this.hasChangesOtherThanFiles ||
          this.allFilesHaveBeenRemoved) /* && !this.hasLiveFileChanges */
      )
    },
    hasChangesOtherThanFiles() {
      return this.changes.some((c) => c.field !== 'files')
    },
    allFilesHaveBeenRemoved() {
      const fileChange = this.changes.find((f) => f.field === 'files')
      return fileChange ? fileChange.updated.length === 0 : false
    },
    titleOfContractChangesSection() {
      if (this.perspective === 'company') {
        return this.hasDraftContract && !this.containsUserChanges
          ? $t('Awaiting acceptance of the following changes')
          : $t("You've made the following changes to the contract")
      }
      return $t('The following changes were made to the contract')
    },
    isViewingAsCompany() {
      return this.perspective === 'company'
    },
    // all changes including database changes and user state changes
    changes() {
      const { baselineContract } = this
      const { comparisonContract } = this
      return this.getDiffs(baselineContract, comparisonContract)
    },
    changedFiles() {
      let changedFiles = []

      if (this.changes.find((f) => f.field === 'files')?.initial?.length) {
        changedFiles = this.changes.find((f) => f.field === 'files')?.initial
      }

      if (this.changes.find((f) => f.field === 'files')?.updated?.length) {
        changedFiles = this.changes.find((f) => f.field === 'files')?.updated
      }

      return changedFiles
    },
    hasFileChanges() {
      return (
        JSON.stringify(this.baselineContract.files) !==
        JSON.stringify(this.comparisonContract.files)
      )
    },
    // these changes are a state-layer on top of the general changes.
    // they track 'live' changes to the form inputs
    userChanges() {
      return this.changes.filter(
        // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
        (change) => this.initialStateOfContract()[change.field] != this.contract[change.field],
      )
    },
    userChangesCount() {
      return this.userChanges.length
    },
    containsUserChanges() {
      return this.hasLiveFileChanges || this.userChanges.length > 0
    },
    changesNeedingApproval() {
      return this.changes.filter((change) => !this.immediatelySavableFields.includes(change.field))
    },
    userChangesNeedingApproval() {
      return this.userChanges.filter(
        (change) => !this.immediatelySavableFields.includes(change.field),
      )
    },
    containsUserChangesNeedingApproval() {
      return this.hasLiveFileChanges || this.userChangesNeedingApproval.length > 0
    },
    companyContactName() {
      return this.companyContacts[this.contract.company_contact_user_id]
    },
    billContactName() {
      return this.companyContacts[this.contract.bill_contact_user_id]
    },
    isAddressFilled() {
      return (
        this.draftContract?.location?.is_filled || this.activeContract?.location?.is_filled || false
      )
    },
    fullAddress() {
      return (
        this.draftContract?.location?.formatted ||
        this.activeContract?.location?.formatted ||
        'Address not set'
      )
    },
    fullCompanyAddress() {
      return (
        this.draftContract?.company_location?.formatted ||
        this.activeContract?.company_location?.formatted ||
        'Address not set'
      )
    },
    initialFullAddress() {
      return (
        this.activeContract?.location?.formatted ||
        this.draftContract?.location?.formatted ||
        'Address not set'
      )
    },
    initialFullCompanyAddress() {
      return (
        this.activeContract?.company_location?.formatted ||
        this.draftContract?.company_location?.formatted ||
        'Address not set'
      )
    },
    paymentTermDisplayText() {
      const isStandardTerms =
        this.contract.payment_term === constantsGlobalVar.payment_term &&
        this.contract.payment_term_method === constantsGlobalVar.payment_term_method
      const textForMethodType =
        this.contract.payment_term_method === 'net'
          ? $t('Net (accounting)')
          : $t('From end of month')
      return `${isStandardTerms ? $t('Standard') : ''} ${this.contract.payment_term} ${$t(
        'days',
      )} ${textForMethodType}`
    },
    timeFrameDisplayText() {
      const startDate = moment(this.contract.date_start, 'YYYY-MM-DD').isValid()
        ? moment(this.contract.date_start, 'YYYY-MM-DD').format('MMM D, YYYY')
        : ''
      const endDate = moment(this.contract.date_end, 'YYYY-MM-DD').isValid()
        ? moment(this.contract.date_end, 'YYYY-MM-DD').format('MMM D, YYYY')
        : ''
      return !startDate && !endDate ? '--' : `${startDate || '?'} - ${endDate || '?'}`
    },
    companyContactsCount() {
      return Object.keys(this.companyContacts).length
    },
    hasCustomConditions() {
      return this.contract.conditions !== undefined && this.contract.conditions.length > 0
    },
    hasCustomInvoiceText() {
      return (
        this.contract.custom_invoice_text !== undefined &&
        this.contract.custom_invoice_text.length > 0
      )
    },
    hasBillContactUserId() {
      return this.contract.bill_contact_user_id !== undefined && this.contract.bill_contact_user_id
    },
    hasAttachedFiles() {
      return this.contract.files !== undefined && this.contract.files.length > 0
    },
    showAdditionalInformation() {
      const showConditions = this.hasCustomConditions || this.advancedFields.includes('conditions')
      const showCustomInvoiceText =
        this.isViewingAsCompany &&
        (this.hasCustomInvoiceText || this.advancedFields.includes('custom_invoice_text'))
      return showConditions || showCustomInvoiceText
    },
    showCustomConditions() {
      return this.hasCustomConditions || this.customConditionsSelected
    },
    dateStartDisabled() {
      return this.hasBills
    },
    companyNameLabelText() {
      let text = 'Company name'
      if (this.perspective === 'company') {
        text = 'Your company name'
        if (this.hasMultipleBusinessEntities) {
          text = 'Contract legal entity'
        }
      }
      return this.$t(text)
    },
    companyAddressLabelText() {
      let text = 'Company address'
      if (this.perspective === 'company') {
        text = 'Your company address'
        if (this.hasMultipleBusinessEntities) {
          text = 'Contract legal entity address'
        }
      }
      return this.$t(text)
    },
    minimumRate() {
      return 1
    },
    enableMultiCurrencyContracts() {
      return !this.company.settings?.disable_multi_currency_contracts
    },
  },

  watch: {
    userChangesCount(newValue) {
      this.$emit('changes-counted', newValue)
    },
    containsUserChanges(newValue) {
      if (newValue) {
        this.$emit('changes-added')
      } else {
        this.$emit('changes-cleared')
      }
    },
    containsUserChangesNeedingApproval(newValue) {
      if (newValue) {
        this.$emit('changes-needing-approval-added')
      } else {
        this.$emit('changes-needing-approval-cleared')
      }
    },
    hasUnreviewedFiles(newValue) {
      if (this.perspective !== 'company') {
        this.$emit('toggled-warnings', newValue)
      }
    },
    termsAccepted() {
      this.hasUnreviewedFiles = !this.termsAccepted
    },
    // Surface the live end date so the parent can cap the scheduled effective
    // date at it (a change can't take effect after the contract ends).
    'contract.date_end': function (newValue) {
      this.$emit('update:date-end', newValue || '')
    },
  },

  created() {
    this.preferWorkerCurrency = this.contract.currency !== this.companyCurrencyConfig.currencySymbol
    this.contract.currency = this.preferWorkerCurrency
      ? this.freelancerCurrencyConfig.currencySymbol
      : this.companyCurrencyConfig.currencySymbol
    if (this.preferWorkerCurrency) {
      this.rateExchanged = this.contract.rate
    } else {
      this.rate = this.contract.rate
    }
  },

  mounted() {
    this.initialChangesSnapshot = this.changes
  },

  methods: {
    $t,
    /**
     * note: only include file changes if the change is that all files have been removed
     * @param baselineContract
     * @param comparisonContract
     * @returns {[]}
     */
    getDiffs(baselineContract, comparisonContract) {
      const changes = []
      /* eslint-disable array-callback-return */
      Object.keys(comparisonContract).map((key) => {
        if (
          // If both values are falsey, consider them as unchanged
          !comparisonContract[key] &&
          !baselineContract[key]
        ) {
          // we want to consider empty strings as equal to nulls
          return
        }
        if (comparisonContract[key] !== baselineContract[key]) {
          const allFilesRemoved =
            key === 'files' &&
            comparisonContract.hasOwnProperty(key) &&
            baselineContract.hasOwnProperty(key)
              ? comparisonContract[key].length === 0 && baselineContract[key].length !== 0
              : false
          if (
            key !== 'status' &&
            key !== 'id' &&
            (key !== 'files' || (key === 'files' && allFilesRemoved))
          ) {
            // handle rate and rate type
            if (key === 'rate' || key === 'ratetype') {
              // change object for both rate and rate type
              const rateChanges = {
                field: 'rate',
                fieldTitle: this.fieldTitles.rate,
                initial: `${baselineContract.rate} ${baselineContract.ratetype}`,
                updated: `${comparisonContract.rate} ${comparisonContract.ratetype}`,
                isPersisted: this.contract.rate === this.initialStateOfContract().rate,
              }

              // if already exists, update
              const i = changes.findIndex((item) => item.field === 'rate')
              if (i > -1) {
                changes[i] = rateChanges
              } else {
                // otherwise push new item
                changes.push(rateChanges)
              }

              // mock ratetype so that intial error handling works
              changes.push({ field: 'ratetype' })
            } else {
              // handle rest of the fields
              changes.push({
                field: key,
                fieldTitle: this.fieldTitles[key],
                initial:
                  key === 'company_contact_user_id' || key === 'bill_contact_user_id'
                    ? this.companyContacts[baselineContract[key]]
                    : baselineContract[key],
                updated:
                  key === 'company_contact_user_id' || key === 'bill_contact_user_id'
                    ? this.companyContacts[comparisonContract[key]]
                    : comparisonContract[key],
                isPersisted: this.contract[key] === this.initialStateOfContract()[key],
              })
            }
          }
        }
      })
      return changes
    },
    focus(ref) {
      nextTick(() => this.$refs[ref].focus())
    },
    // Feed the props into the initial contract state
    initialStateOfContract() {
      return {
        id:
          this.draftContract.id ||
          this.activeContract.id ||
          this.contractBuiltFromOtherModels.id ||
          0,
        job_name:
          this.draftContract.job_name || this.activeContract.job_name || this.job?.name || '',
        job_description:
          this.draftContract.job_description ||
          this.activeContract.job_description ||
          this.job?.description ||
          '',
        job_location_preference:
          this.draftContract.job_location_preference ||
          this.activeContract.job_location_preference ||
          this.contractBuiltFromOtherModels.job_location_preference ||
          '',
        rate:
          this.draftContract.rate ||
          this.activeContract.rate ||
          this.contractBuiltFromOtherModels.rate ||
          0,
        ratetype:
          this.draftContract.ratetype ||
          this.activeContract.ratetype ||
          this.contractBuiltFromOtherModels.ratetype ||
          '',
        currency:
          this.draftContract.currency ||
          this.activeContract.currency ||
          this.contractBuiltFromOtherModels.currency ||
          'DKK',
        address:
          this.draftContract?.location?.address ||
          this.draftContract?.location?.legacy_address ||
          this.activeContract?.location?.address ||
          this.activeContract?.location?.legacy_address ||
          this.contractBuiltFromOtherModels.address ||
          this.company.address ||
          '',
        city:
          this.draftContract?.location?.city ||
          this.activeContract?.location?.city ||
          this.contractBuiltFromOtherModels.city ||
          this.company.city ||
          '',
        zipcode:
          this.draftContract?.location?.zipcode ||
          this.activeContract?.location?.zipcode ||
          this.contractBuiltFromOtherModels.zipcode ||
          this.company.zipcode ||
          '',
        country:
          this.draftContract?.location?.country ||
          this.activeContract?.location?.country ||
          this.contractBuiltFromOtherModels.country ||
          this.company.country ||
          '',
        state:
          this.draftContract?.location?.state ||
          this.activeContract?.location?.state ||
          this.contractBuiltFromOtherModels.state ||
          this.company.state ||
          '',
        date_start:
          this.draftContract.date_start ||
          this.activeContract.date_start ||
          this.contractBuiltFromOtherModels.date_start ||
          '',
        date_end:
          this.draftContract.date_end ||
          this.activeContract.date_end ||
          this.contractBuiltFromOtherModels.date_end ||
          '',
        company_contact_user_id:
          this.draftContract.company_contact_user_id ||
          this.activeContract.company_contact_user_id ||
          this.contractBuiltFromOtherModels.company_contact_user_id ||
          this.company.user_id ||
          '',
        bill_contact_user_id:
          this.draftContract.bill_contact_user_id || this.activeContract.bill_contact_user_id || '',
        po_number:
          this.draftContract.po_number ||
          this.activeContract.po_number ||
          this.contractBuiltFromOtherModels.po_number ||
          '',
        custom_invoice_text:
          this.draftContract.custom_invoice_text ||
          this.activeContract.custom_invoice_text ||
          this.contractBuiltFromOtherModels.custom_invoice_text ||
          '',
        conditions:
          this.draftContract.conditions ||
          this.activeContract.conditions ||
          this.contractBuiltFromOtherModels.conditions ||
          '',
        payment_term:
          this.draftContract.payment_term ||
          this.activeContract.payment_term ||
          this.contractBuiltFromOtherModels.payment_term ||
          this.company.payment_term ||
          '',
        payment_term_method:
          this.draftContract.payment_term_method ||
          this.activeContract.payment_term_method ||
          this.contractBuiltFromOtherModels.payment_term_method ||
          this.company.payment_term_method ||
          '',
        company_name:
          this.draftContract.company_name ||
          this.activeContract.company_name ||
          this.contractBuiltFromOtherModels.company_name ||
          this.company.company ||
          '',
        company_address:
          this.draftContract?.company_location?.address ||
          this.activeContract?.company_location?.address ||
          this.contractBuiltFromOtherModels.company_address ||
          this.company.address ||
          '',
        company_zipcode:
          this.draftContract?.company_location?.zipcode ||
          this.activeContract?.company_location?.zipcode ||
          this.contractBuiltFromOtherModels.company_zipcode ||
          this.company.zipcode ||
          '',
        company_city:
          this.draftContract?.company_location?.city ||
          this.activeContract?.company_location?.city ||
          this.contractBuiltFromOtherModels.company_city ||
          this.company.city ||
          '',
        company_country: (
          this.draftContract?.company_location?.country ||
          this.activeContract?.company_location?.country ||
          this.contractBuiltFromOtherModels.company_country ||
          this.company.country ||
          this.company.market ||
          ''
        ).toUpperCase(),
        company_state: (
          this.draftContract?.company_location?.state ||
          this.activeContract?.company_location?.state ||
          this.contractBuiltFromOtherModels.company_state ||
          this.company.state ||
          ''
        ).toUpperCase(),
        company_cvr:
          this.draftContract.company_cvr ||
          this.activeContract.company_cvr ||
          this.contractBuiltFromOtherModels.company_cvr ||
          this.company.cvr ||
          '',
        files:
          this.draftContract.files ||
          this.activeContract.files ||
          this.contractBuiltFromOtherModels.files ||
          [],
        factoring_days:
          this.draftContract.factoring_days ||
          this.activeContract.factoring_days ||
          this.contractBuiltFromOtherModels.factoring_days ||
          null,
        freelancer_name:
          this.draftContract.freelancer_name ||
          this.activeContract.freelancer_name ||
          this.contractBuiltFromOtherModels.freelancer_name ||
          null,
        recruiter_manages_workers:
          this.draftContract.recruiter_manages_workers ||
          this.activeContract.recruiter_manages_workers ||
          this.contractBuiltFromOtherModels.recruiter_manages_workers ||
          false,
        recruiter_company:
          this.draftContract.recruiter_company ||
          this.activeContract.recruiter_company ||
          this.contractBuiltFromOtherModels.recruiter_company ||
          null,
        recruiter_contact:
          this.draftContract.recruiter_contact ||
          this.activeContract.recruiter_contact ||
          this.contractBuiltFromOtherModels.recruiter_contact ||
          null,
      }
    },
    resetToInitialFull() {
      this.contract = this.initialStateOfContract()
    },
    resetToInitialField(fieldName) {
      this.contract[fieldName] = this.initialStateOfContract()[fieldName]
    },
    toggleHasLiveFileChanges(hasChanges) {
      this.hasLiveFileChanges = hasChanges
    },
    registerAttemptToEditCompanyFields() {
      this.hasAttemptedToEditCompanyFields = true
    },
    locationPreferenceLabel(location) {
      switch (location) {
        case 'onsite-only':
          return $t('On-site')
        case 'remote-only':
          return $t('Remote')
        default:
          return $t('No preference')
      }
    },
    truncateText: textHelpers.truncate,
    getFieldByKey(key) {
      const matchedFields = this.changes.filter((item) => item.field === key)
      if (matchedFields.length < 1) {
        return null
      }

      return matchedFields.pop()
    },
    isFieldByKeyUpdated(key) {
      const changedField = this.getFieldByKey(key)

      if (changedField && this.showChanges && changedField.initial !== changedField.updated) {
        return true
      }

      return false
    },
    canShowUpdatedLabelByField(key) {
      return this.isFieldByKeyUpdated(key)
    },
    isLocationFieldUpdated(field) {
      const draftContractLocation = this.draftContract?.location ?? {}
      const activeContractLocation = this.activeContract?.location ?? {}

      const oldValue = draftContractLocation[field] ?? null
      const newValue = activeContractLocation[field] ?? null

      return oldValue !== null && newValue !== null && oldValue !== newValue
    },
    isCompanyLocationFieldUpdated(field) {
      const draftContractLocation = this.draftContract?.company_location ?? {}
      const activeContractLocation = this.activeContract?.company_location ?? {}

      const oldValue = draftContractLocation[field] ?? null
      const newValue = activeContractLocation[field] ?? null

      return oldValue !== null && newValue !== null && oldValue !== newValue
    },
    isAddressChanged() {
      return (
        this.isLocationFieldUpdated('address') ||
        this.isLocationFieldUpdated('city') ||
        this.isLocationFieldUpdated('zipcode') ||
        this.isLocationFieldUpdated('country') ||
        this.isLocationFieldUpdated('state')
      )
    },
    isCompanyAddressChanged() {
      return (
        this.isCompanyLocationFieldUpdated('address') ||
        this.isCompanyLocationFieldUpdated('city') ||
        this.isCompanyLocationFieldUpdated('zipcode') ||
        this.isCompanyLocationFieldUpdated('country') ||
        this.isCompanyLocationFieldUpdated('state')
      )
    },
    canShowAddressUpdatedLabel() {
      return this.isAddressChanged()
    },
    canShowCompanyAddressUpdatedLabel() {
      return this.isCompanyAddressChanged() && !this.isViewingAsCompany
    },
    isTimeframeChanged() {
      return this.isFieldByKeyUpdated('date_start') || this.isFieldByKeyUpdated('date_end')
    },
    canShowTimeframeUpdatedLabel() {
      return this.isTimeframeChanged() && !this.isViewingAsCompany
    },
    getChangedPaymentTermDisplayText(paymentTerm, paymentTermMethod) {
      const isStandardTerms =
        paymentTerm === constantsGlobalVar.payment_term &&
        paymentTermMethod === constantsGlobalVar.payment_term_method
      const textForMethodType =
        paymentTerm === 'net' ? $t('Net (accounting)') : $t('From end of month')

      return `${isStandardTerms ? $t('Standard') : ''} ${paymentTerm} ${$t(
        'days',
      )} ${textForMethodType}`
    },
    getTimeframeDisplayText() {
      const start = this.getFieldByKey('date_start')?.initial ?? this.contract.date_start
      const end = this.getFieldByKey('date_end')?.initial ?? this.contract.date_end

      const startDate = moment(start, 'YYYY-MM-DD').isValid()
        ? moment(start, 'YYYY-MM-DD').format('MMM D, YYYY')
        : ''
      const endDate = moment(end, 'YYYY-MM-DD').isValid()
        ? moment(end, 'YYYY-MM-DD').format('MMM D, YYYY')
        : ''
      return !startDate && !endDate ? '--' : `${startDate || '?'} - ${endDate || '?'}`
    },
    onTogglePreferCurrency(val) {
      this.preferWorkerCurrency = val
      const newCurrency = val
        ? this.freelancerCurrencyConfig.currencySymbol
        : this.companyCurrencyConfig.currencySymbol
      this.$emit('currency-change', {
        currentCurrency: this.contract.currency,
        newCurrency,
        flCurrency: this.freelancerCurrencyConfig.currencySymbol,
      })
      this.contract.currency = newCurrency
      this.contract.rate = val ? this.rateExchanged : this.rate
    },
    onRateUpdated(val) {
      if (!this.preferWorkerCurrency) {
        this.contract.rate = val
      }
      this.rate = val
    },
    onRateExchangedUpdated(val) {
      if (this.preferWorkerCurrency) {
        this.contract.rate = val
      }
      this.rateExchanged = val
    },
    handleToggleEdit(ref) {
      this.focus(ref)
      this.registerAttemptToEditCompanyFields()
    },
    toggleEditCompanyAddress() {
      this.handleToggleEdit('company_address')

      this.wantsToFillCompanyAddress = true
    },
  },
}
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
</style>
